import React, { useMemo } from 'react';
import { Stack, Typography, Link } from '@mui/material';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { TextField } from 'components/TextField/TextField';
import { Select } from 'components/Select/Select';
import { DateField } from 'components/DateField/DateField';
import { PhoneInput } from 'components/PhoneInput/PhoneInput';
import { Control, Controller, useFormState } from 'react-hook-form';
import {
  isValidDateOfBirth,
  isValidEmailAddress,
} from 'utils/inputProcessingUtils/inputProcessingUtils';
import { getContactLinkDataWithDefault } from 'utils';
import { isValidPhoneNumber } from 'utils/phoneNumberUtils/phoneNumberUtils';

interface SignUpHelpDialogFormProps {
  showInvitedByField?: boolean;
  formControl: Control<HelpDialogData, any>;
  invitedByoptions: { label: string; value: string }[];
}

export const SignUpHelpDialogForm = ({
  showInvitedByField,
  formControl,
  invitedByoptions,
}: SignUpHelpDialogFormProps) => {
  const { translation } = useTranslationContext();
  const checkIfPhoneNumberValid = (input: string): boolean => !input || isValidPhoneNumber(input);
  const contactLinkData = getContactLinkDataWithDefault();

  return (
    <Stack spacing={4} id="help-dialog-form">
      <Typography variant="body1" id="help-dialog-form__instructions">
        {translation('helper_text_ios')}
        <Link
          href={translation('eligibility_error_support_link_url')}
          underline="hover"
          target="_blank"
          rel="noopener noreferrer"
          id="help-dialog-form__support-link"
        >
          {translation('eligibility_error_support_link_display')}
        </Link>
      </Typography>
      <Controller
        name="memberID"
        control={formControl}
        rules={{ required: translation('empty_field_error') }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="help-dialog-form__member-id"
            label={translation('enter_member_id')}
            helperText={error?.message || translation('enter_member_id_subtext')}
            error={!!error}
            inputRef={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <Controller
        name="name"
        control={formControl}
        rules={{ required: translation('empty_field_error') }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="help-dialog-form__name"
            label={translation('enter_your_name')}
            error={!!error}
            helperText={error?.message}
            inputRef={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <Controller
        name="email"
        control={formControl}
        rules={{
          required: translation('empty_field_error'),
          validate: (input: string) =>
            isValidEmailAddress(input) || translation('email_invalid_error'),
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="help-dialog-form__email"
            label={translation('enter_your_email_address')}
            error={!!error}
            helperText={error?.message}
            inputRef={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      {showInvitedByField && (
        <Controller
          name="invitedBy"
          control={formControl}
          rules={{ required: translation('empty_field_error') }}
          render={({ field, fieldState: { error } }) => (
            <Select
              id="help-dialog-form__invited-by"
              label={translation('invited_by_title')}
              options={invitedByoptions}
              error={!!error}
              helperText={error?.message}
              inputRef={field.ref}
              value={field?.value || ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      <Controller
        name="dateOfBirth"
        control={formControl}
        rules={{
          required: translation('empty_field_error'),
          validate: (input: string) =>
            isValidDateOfBirth(input) || translation('wrong_date_of_birth'),
        }}
        render={({ field, fieldState: { error } }) => (
          <DateField
            id="help-dialog-form__date-of-birth"
            label={translation('date_of_birth')}
            error={!!error}
            helperText={error?.message}
            inputRef={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <Controller
        name="phoneNumber"
        control={formControl}
        rules={{
          validate: (input: string) =>
            checkIfPhoneNumberValid(input) || translation('phone_number_invalid_error'),
        }}
        render={({ field, fieldState: { error } }) => (
          <PhoneInput
            id="help-dialog-form__phone-number"
            label={`${translation('phone_number')} ${translation('optional_field')}`}
            error={!!error}
            helperText={error?.message}
            inputRef={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <Typography variant="body1" id="help-dialog-form__contact-info">
        {translation('reach_us_at_part1_ios')}
        <Link
          href={contactLinkData.url}
          underline="hover"
          id="help-dialog-form__phone-link"
          aria-label={`Call support at ${contactLinkData.displayValue}`}
        >
          {contactLinkData.displayValue}
        </Link>
        {translation('reach_us_at_part2_ios')}
      </Typography>
    </Stack>
  );
};
